import {
  ALL_MODULES,
  DateTimeFormatter,
  getHtmlFromMarkDown,
  TICKET_SOURCE,
  EventEmitterWithIndex,
  AsyncPropSelector,
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP,
  COMMON_CONSTANTS,
  toast
} from '@nile/components';
import AIFeedback from '@nile/components/src/Components/AI/components/AIFeedback';
import {
  dompurifier,
  EventEmitter,
  getCurrentUserHook,
  getNotificationTemplateByConstantHook,
  getTranslatedText
} from '@nile/components/src/Utils/helpers';
import {
  AccountClient,
  GET_CLIENT_USERS,
  SAVE_TICKET_CONVERSATION,
  SAVE_TICKET_INTEGRATION_CONVERSATION,
  TicketClient,
  UPDATE_TICKET
} from '@nile/graphql';
import { GET_TICKET_REPLY_RECIPEINTS } from '@nile/graphql/src/Query/ticket';
import envConfig from '@nile/home/src/Config/env';
import GlobalTicketConversationAction from '@nile/mangifera/src/Containers/ChatContainers/GlobalTicketConversationAction';
import GlobalTicketConversationUsers from '@nile/mangifera/src/Containers/ChatContainers/GlobalTicketConversationUsers';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  Card,
  Link,
  TextStyle,
  Banner,
  DisplayText,
  SkeletonBodyText
} from '@superops/ui-design-system';
import { isEmpty, cloneDeep, filter, forEach } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import TriageFeedback from '@nile/components/src/Components/AI/components/AITicketTriage/TriageFeedback';
import ClosureFields from '@nile/mangifera/src/Components/Ticket/ClosureFields';
import { useMutation } from '@apollo/client';
import { UPDATE_TRIAGE_TICKET } from '@nile/banyan/src/Graphql/Mutations/ai';
import { GET_TRIAGE_TICKET_DATA } from '@nile/banyan/src/Graphql/Query/ai';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';
import { getSuggestedFields } from '@nile/components/src/Components/AI/components/AITicketTriage/AITicketTriage';
import TriageFeedbackWithPopup from '@nile/components/src/Components/AI/components/AITicketTriage/TriageFeedbackWithPopup.tsx';
import TicketConversationActionHook from '@nile/mangifera/src/Containers/ChatContainers/TicketConversationActionHook';
import { useFlags } from 'launchdarkly-react-client-sdk';

const DislikeCardContent = () => {
  return (
    <div className="p10">
      <div className="flex actr jctr">
        <SVGIcon name="ThumbsDownOutline" color="#CCCCCC" size="xl" />
      </div>
      <div className="mt10">
        <TextStyle>
          <div className="fsize12 tcenter">
            <FormattedMessage id="ai.ticket.triage.fields.dislike.message" />
          </div>
        </TextStyle>
      </div>
    </div>
  );
};

const CustomLabel = ({ label }) => {
  return (
    <div className="fsize12 mw-40p flex actr">
      <TextStyle variation="subdued" weight="Medium">
        <div className="ellipsis">{label} :</div>
      </TextStyle>
    </div>
  );
};

const TicketDetails = ({ isDisliked, TicketIdComponent, data }) => {
  const clientName = data?.client?.name;
  const requesterName = data?.requester?.name;
  const ticketId = data?.ticketId;
  const status = data?.status;

  const tabContext = useContext(TabContext);

  const openTicket = () => {
    tabContext.openTab({
      module: 'TICKET',
      ticketId: ticketId,
      data: {
        ticketId: ticketId
      }
    });
  };
  return (
    <>
      <div className={`flex actr fx-sb mb10  ${isDisliked ? 'opacity6' : ''}`}>
        <div className="pl20">
          <TicketIdComponent ticket={data} />
        </div>
        <div>
          <Badge size="large" bold status="info">
            {status}
          </Badge>
        </div>
      </div>
      <div className="w100">
        {/* Client and requester details */}
        <div
          className={`flex g5 actr mb10 w100 pl20 ${
            isDisliked ? 'opacity6' : ''
          }`}
          id="client-requester-details"
        >
          <div className="mw-50p flex actr g5 ellipsis mw-0">
            <div className="ellipsis mw-0">
              <DisplayText element="p" size="small">
                {clientName}
              </DisplayText>
            </div>
            <DisplayText element="p" size="small" variant="subdued">
              From
            </DisplayText>
            <div className="ellipsis mw-0">
              <DisplayText
                element="p"
                size="small"
                variant={requesterName ? 'default' : 'subdued'}
              >
                <div className="ellipsis mw-0">{requesterName || 'NA'}</div>
              </DisplayText>
            </div>
          </div>
          <div className="mw-50p flex actr g5 ellipsis mw-0">
            <DisplayText element="p" size="small" variant="subdued">
              On
            </DisplayText>
            <div className="ellipsis mw-0">
              <DisplayText element="p" size="small">
                <DateTimeFormatter type="dateTime" date={data?.createdTime} />
              </DisplayText>
            </div>
          </div>
        </div>
        {/* End client and requester details */}

        <div
          id="ticket-subject"
          className={`mb30 ellipsis w100 pl20  ${isDisliked ? 'opacity6' : ''}`}
        >
          <Link onClick={openTicket} fontWeight="bolder">
            {data?.subject}
          </Link>
        </div>
      </div>
    </>
  );
};

const TriageTicketCard = (props) => {
  const {
    data,
    onFeedbackComplete,
    ticketIdComponent,
    dislikedTicketData
  } = props;

  const { ticketId } = data;

  const flags = useFlags();
  const ticketConversationUx = flags?.ticketConversationUx;
  const ticketDataForConversation = {
    id: ticketId,
    ticketId: ticketId,
    subject: data?.subject,
    requester: data?.requester,
    client: data?.client,
    displayId: data?.displayId,
    source: data?.source
  };

  const { openChatPopup, conversationLoading } = TicketConversationActionHook({
    ticket: ticketDataForConversation
  });

  const TicketIdComponent = ticketIdComponent || <></>;
  const intl = useIntl();
  const currentUser = getCurrentUserHook();

  const isReplyDisliked = dislikedTicketData?.feedbackType?.includes(
    'SUGGESTION_CLARIFICATION'
  );
  const isFieldsDisliked = dislikedTicketData?.feedbackType?.includes(
    'SUGGESTED_FIELDS'
  );

  const [replyResponse, setReplyResponse] = useState(null);
  const [fieldsMeta, setFieldsMeta] = useState(null);
  const [fieldsData, setFieldsData] = useState(null);
  const [closureFieldsData, setClosureFieldsData] = useState(null);
  const [techGroupData, setTechGroupData] = useState(null);
  const [isReplyPopedOut, setIsReplyPopedOut] = useState(false);

  const getIndividualTriageTicketData = async () => {
    try {
      const response = await TicketClient.query({
        query: GET_TRIAGE_TICKET_DATA,
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
        variables: {
          id: ticketId
        }
      });
      return response?.data?.getTicket;
    } catch (error) {
      console.error('Error fetching triage ticket data:', error);
      return null;
    }
  };

  const [updateTicket] = useMutation(UPDATE_TRIAGE_TICKET, {
    client: TicketClient
  });

  const tabContext = useContext(TabContext);

  const generateReceiverOptions = (users) => {
    const requester = data?.requester || null;
    const additionalRequesters = data?.additionalRequester || [];
    const currentUserId = currentUser?.userId;

    //  Filter users with roleType TECHNICIAN and not the current user
    const technicianUsers =
      users?.filter(
        (user) =>
          user?.user?.role?.roleType?.type === 'TECHNICIAN' &&
          user?.user?.userId !== currentUserId
      ) || [];
    // Get requester and additionalRequesters, filtering out the current user
    const otherRequesters = [requester, ...additionalRequesters]
      ?.filter((user) => user && user?.userId && user?.userId !== currentUserId)
      .map((user) => {
        return {
          user: user
        };
      });
    // Merge techs and requesters
    const allFilterUsers = [...technicianUsers, ...otherRequesters];
    // Deduplicate by userId
    const uniqueUsers = allFilterUsers?.filter(
      (user, index, self) =>
        index === self.findIndex((u) => u?.user?.userId === user?.user?.userId)
    );
    return uniqueUsers?.map((user) => {
      return {
        ...user,
        label: user?.user?.name,
        value: user?.user?.email
      };
    });
  };

  const getReplyRecipients = async () => {
    const response = await TicketClient.query({
      query: GET_TICKET_REPLY_RECIPEINTS,
      variables: {
        ticketInput: {
          ticketId: ticketId
        }
      }
    });

    const formatUsers = (users) => {
      if (users && users?.length > 0) {
        return users.map((user) => ({
          ...user,
          label: user?.name,
          value: user?.name
        }));
      }
      return [];
    };

    const recipientsData = response?.data?.getTicketConversationReplyRecipients;
    const selectedMailbox = recipientsData?.selectedMailbox;
    const toUsers = formatUsers(recipientsData?.to);
    const ccUsers = formatUsers(recipientsData?.cc);
    const bccUsers = formatUsers(recipientsData?.bcc);

    return {
      selectedMailbox,
      toUsers,
      ccUsers,
      bccUsers
    };
  };

  const [actions, setActions] = useState(null);

  const featureId = data?.aiAgentActionData?.aiFeature?.featureId;

  const initializeClosureFields = () => {
    if (isEmpty(closureFieldsData)) {
      let initClosureFields = {};
      const fieldsDataArr = Object.entries(fieldsData);
      fieldsDataArr.forEach(([key, value]) => {
        const fieldMeta = fieldsMeta.find((meta) => meta.columnName === key);
        if (fieldMeta) {
          if (fieldMeta.fieldCategory === 'CUSTOM') {
            const currentCustomFields = initClosureFields?.customFields || {};
            initClosureFields = {
              ...initClosureFields,
              customFields: {
                ...currentCustomFields,
                [key]: value
              }
            };
          } else {
            initClosureFields = {
              ...initClosureFields,
              [key]: value
            };
          }
        }
      });
      setClosureFieldsData(initClosureFields);
    }
  };

  const getChilds = (field) => {
    const result = [];
    function findChildren(parentColumnName) {
      if (!parentColumnName) return;
      const children = filter(
        fieldsMeta,
        (field) => field?.parentField?.columnName === parentColumnName
      );
      result.push(...children);
      forEach(children, (child) => findChildren(child?.columnName));
    }
    findChildren(field?.columnName);
    return result;
  };

  const onClosureFieldsChange = (field, value) => {
    const fieldName = field?.columnName;
    const isCustomField = field?.fieldCategory === 'CUSTOM';
    const updatedValue = value?.value;
    let updatedFieldsData = { ...fieldsData };
    let updatedClosureFieldsData = cloneDeep(closureFieldsData);
    const childs = getChilds(field);

    if (childs?.length > 0) {
      childs.forEach((child) => {
        const colName = child?.columnName;
        updatedFieldsData = { ...updatedFieldsData, [colName]: null };
        if (child?.fieldCategory === 'CUSTOM') {
          const closureCustomFields =
            updatedClosureFieldsData?.customFields || {};
          updatedClosureFieldsData = {
            ...updatedClosureFieldsData,
            customFields: {
              ...closureCustomFields,
              [colName]: null
            }
          };
        } else {
          updatedClosureFieldsData = {
            ...updatedClosureFieldsData,
            [colName]: null
          };
        }
      });
    }

    if (fieldName && updatedValue) {
      if (isCustomField) {
        let customFieldVariables = updatedClosureFieldsData?.customFields || {};
        customFieldVariables = {
          ...customFieldVariables,
          [fieldName]: updatedValue
        };
        updatedClosureFieldsData.customFields = customFieldVariables;
        updatedFieldsData[fieldName] = updatedValue;
      } else {
        updatedClosureFieldsData[fieldName] = updatedValue;
        updatedFieldsData[fieldName] = updatedValue;
      }

      setFieldsData(updatedFieldsData);
      setClosureFieldsData(updatedClosureFieldsData);
    }
  };

  const [baseFieldsData, setBaseFieldsData] = useState(null);
  const [baseTechGroupData, setBaseTechGroupData] = useState(null);

  const getFieldSuggestions = () => {
    const fieldSuggestions = getSuggestedFields({
      baseTechGroup: baseTechGroupData,
      updatedTechGroup: techGroupData,
      baseFields: baseFieldsData,
      updatedFields: fieldsData
    });
    return fieldSuggestions;
  };

  const formatActions = (actionsData) => {
    if (!actionsData) {
      setFieldsData(null);
      setTechGroupData(null);
    }
    let selectedFormData;
    let selectedTechGroupData;
    let ticketFieldsData = {};

    actionsData?.forEach((action) => {
      if (action?.actionValueType === 'TICKET_REPLY') {
        setReplyResponse(action);
      }
      if (action?.actionValueType === 'TICKET_UPDATE') {
        const fieldName = action?.name;
        const fieldValue = action?.actionValueData;
        const formattedData = {
          [fieldName]: fieldValue
        };
        if (action?.name === 'form') {
          selectedFormData = {
            ...fieldValue,
            label: fieldValue?.name,
            value: fieldValue?.name
          };
        } else if (action?.name === 'techGroup') {
          selectedTechGroupData = {
            ...fieldValue,
            label: fieldValue?.name,
            value: fieldValue?.name
          };
        } else {
          ticketFieldsData = { ...ticketFieldsData, ...formattedData };
        }
      }
    });
    setFieldsData(ticketFieldsData);
    setTechGroupData(selectedTechGroupData);
    setBaseFieldsData(ticketFieldsData || null);
    setBaseTechGroupData(selectedTechGroupData || null);
  };

  const updateAllFields = (fieldsMetaData) => {
    if (!fieldsMeta) {
      setFieldsMeta(fieldsMetaData);
    }
  };

  const replyContent = replyResponse?.actionValueData?.content || '';

  const formattedReplyContent = getHtmlFromMarkDown(replyContent);

  const isBothFieldsAndReplyPresent =
    !!replyResponse && (!!closureFieldsData || techGroupData);

  const getTriageReviewSourceAction = () => {
    if (isBothFieldsAndReplyPresent) {
      return null;
    }
    if (!replyResponse) {
      return ['SUGGESTED_FIELDS'];
    }
    return ['SUGGESTION_CLARIFICATION'];
  };
  const triageReviewSourceAction = getTriageReviewSourceAction();

  const formatData = useCallback((params) => {
    const ccUsersFormatted = [];
    const bccUsersFormatted = [];
    let recipientsData = [];
    const attachments = params?.template?.attachments
      ? params?.template?.attachments.map((el) => {
          const { fileName, originalFileName, fileSize } = el;
          return {
            fileName,
            originalFileName,
            fileSize
          };
        })
      : [];
    params?.ccUsers?.forEach((el) => {
      if (el?.email) {
        ccUsersFormatted.push({
          email: el?.email
        });
      } else if (el?.__isNew__) {
        ccUsersFormatted.push({
          email: el?.value
        });
      }
    });
    params?.bccUsers?.forEach((el) => {
      if (el?.email) {
        bccUsersFormatted.push({
          email: el?.email
        });
      } else if (el?.__isNew__) {
        bccUsersFormatted.push({
          email: el?.value
        });
      }
    });
    let variables = {
      ticketId: params?.data?.ticketId,
      content: params?.template?.content,
      attachments: attachments,
      ticketInput: {
        handledByAIAgent: {
          featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
        }
      }
    };

    // For private conversation - Instant Messaging
    if (params?.data?.source === TICKET_SOURCE.Communication) {
      if (params?.conversationScope === 'PRIVATE') {
        recipientsData = params?.recipients?.map((el) => {
          return {
            user: {
              userId: el?.user?.userId
            },
            referenceId: el?.referenceId || null,
            label: el?.user?.email, // send email as label
            name: el?.user?.name,
            recipientType: 'RECEIVER'
          };
        });
      }
      const senderPresentInReceipeint = recipientsData?.some(
        (el) => (el?.user?.email || el?.label) === currentUser?.email
      );
      if (!senderPresentInReceipeint) {
        recipientsData = [
          ...recipientsData,
          {
            user: {
              userId: currentUser?.userId
            },
            referenceId: null,
            label: currentUser?.email,
            name: currentUser?.name,
            recipientType: 'SENDER'
          }
        ];
      }
    }

    if (params?.data?.source === TICKET_SOURCE.Communication) {
      variables = {
        ...variables,
        sendMail: true,
        conversationScope: params?.conversationScope,
        recipientDetails: recipientsData
      };
    } else {
      variables = {
        ...variables,
        toUsers: [
          {
            email: params?.toUsers?.email
          }
        ],
        ccUsers: ccUsersFormatted,
        bccUsers: bccUsersFormatted,
        fromMailbox: { boxId: params?.data?.selectedMailbox?.boxId },
        inlineImages: params?.template?.inlineImages
      };
    }

    return variables;
  }, []);

  const [sendReplyLoading, setSendReplyLoading] = useState(false);
  const [fieldsUpdating, setFieldsUpdating] = useState(false);
  const [fieldsApproved, setFieldsApproved] = useState(false);
  const { getGlobalConversations } = useGlobalChatHook();

  const globalConversations = getGlobalConversations();

  const checkIsReplyPopedOut = () => {
    const isPopedOut = globalConversations.find((conv) => {
      const ticketIdFromPopup =
        conv?.data?.ticketId ||
        conv?.data?.ticket?.ticketId ||
        conv?.data?.ticket?.id;
      return ticketIdFromPopup === ticketId;
    });

    setIsReplyPopedOut(isPopedOut);
    return isPopedOut;
  };

  const notificationTemplateByConstant = getNotificationTemplateByConstantHook({
    notificationTemplateType: 'SYSTEM_TICKETING',
    constant: 'TICKET_REQ_CLOSED'
  });

  const checkHideGlobalTicketConversationCloseModal = (chat) => {
    const activeTicketId = tabContext?.activeTab?.data?.ticketId;
    return activeTicketId === chat?.id;
  };

  const onUpdateComplete = async () => {
    const updatedTicketData = await getIndividualTriageTicketData();
    if (updatedTicketData) {
      const actionData =
        updatedTicketData?.aiAgentActionData?.action?.length > 0
          ? updatedTicketData?.aiAgentActionData?.action
          : null;
      setActions(actionData);
      formatActions(actionData);
    }
  };

  const openTicket = () => {
    tabContext.openTab({
      module: 'TICKET',
      ticketId: ticketId,
      data: {
        ticketId: ticketId
      }
    });
  };

  const updateTicketFields = () => {
    const fieldsInput = closureFieldsData || {};
    let variables = {
      id: ticketId,
      ...fieldsInput,
      handledByAIAgent: {
        featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
      }
    };

    if (techGroupData?.groupId && techGroupData?.groupId) {
      variables = {
        ...variables,
        techGroup: {
          name: techGroupData?.name,
          groupId: techGroupData?.groupId
        }
      };
    }

    setFieldsUpdating(true);
    updateTicket({
      variables: variables
    })
      .then((res) => {
        if (res?.data?.updateTicket) {
          setFieldsUpdating(false);
          onUpdateComplete();
          setFieldsApproved(true);
        }
      })
      .catch((err) => {
        console.log(err);
        setFieldsUpdating(false);
      });
  };

  const sendReplyContent = async () => {
    if (!data?.requester) {
      return;
    }
    if (ticketConversationUx) {
      const isChatActive = checkIsReplyPopedOut();
      if (isChatActive) {
        EventEmitter.dispatch('insertTriageTicketConversation', {
          id: ticketId,
          template: {
            content: formattedReplyContent
          }
        });
      } else {
        openChatPopup({
          mode: 'conversation',
          prefillTemplate: { content: formattedReplyContent || '' },
          triageConversation: true
        });
      }
      return;
    }
    setSendReplyLoading(true);
    const {
      selectedMailbox,
      toUsers,
      ccUsers,
      bccUsers
    } = await getReplyRecipients();

    if (isEmpty(toUsers) || isEmpty(selectedMailbox)) {
      setSendReplyLoading(false);
      return;
    }

    const attachmentUploadUrl = `${envConfig.fileRoot}/upload`;
    const uploadParams = {
      module: ALL_MODULES.ticketConversationAttachment,
      details: JSON.stringify({ ticketId: ticketId })
    };
    const defaultTemplate = {
      name: '',
      content: dompurifier(formattedReplyContent || ''),
      placeholdersUsed: '',
      mentions: [],
      attachments: [],
      inlineImages: []
    };
    const usersConfig = {
      property: 'userId',
      query: GET_CLIENT_USERS,
      params: {
        client: AccountClient,
        variables: { input: { clientId: data?.client?.accountId } }
      },
      methodName: 'getClientUserAssociationsList'
    };

    const recipientsConfig = {
      property: 'recipientId',
      query: GET_CLIENT_USERS,
      params: {
        client: AccountClient,
        variables: { input: { clientId: data?.client?.accountId } }
      },
      methodName: 'getClientUserAssociationsList',
      generateOptions: generateReceiverOptions,
      isMulti: true,
      field: 'recipients',
      placeholder: getTranslatedText(intl, 'common.choose.technician'),
      errors: '',
      styles: {
        multiValueRemove: (base, state) => {
          if (state.data.isFixed) {
            return { ...base, display: 'none' };
          }
          return base;
        }
      },
      isClearable: false
    };

    const CHAT_PARAMS = {
      id: ticketId,
      subject: data?.subject,
      module: 'TICKET',
      data: {
        ticketId,
        subject: data?.subject,
        source: data?.source,
        selectedMailbox: selectedMailbox,
        requester: data?.requester,
        notificationTemplateByConstant,
        id: ticketId,
        displayId: data?.displayId,
        client: data?.client,
        additionalRequester: data?.additionalRequester,
        isTriageConversation: true
      },
      ccUsers: ccUsers,
      bccUsers: bccUsers,
      toUsers: data?.requester,
      recipients: [],
      conversationScope: 'PUBLIC',
      recipientsConfig: { ...recipientsConfig },
      toUsersConfig: usersConfig,
      ccUsersConfig: { ...usersConfig, isMulti: true, creatable: true },
      bccUsersConfig: { ...usersConfig, isMulti: true },
      formatData: formatData,
      template: defaultTemplate,
      editorDetails: {},
      mutationClient: TicketClient,
      mutationAPI:
        data?.source === TICKET_SOURCE.Communication
          ? SAVE_TICKET_INTEGRATION_CONVERSATION
          : SAVE_TICKET_CONVERSATION,
      callBack: () => {},
      defaultTemplate: defaultTemplate,
      attachmentUploadParams: uploadParams,
      attachmentUploadUrl: attachmentUploadUrl,
      checkHideCloseModal: checkHideGlobalTicketConversationCloseModal,
      onOpen: () => {},
      onTitleClick: openTicket,
      primaryAction: GlobalTicketConversationAction,
      onClose: () => {
        EventEmitterWithIndex.dispatch('insertTriageTicketConversation', {
          content: formattedReplyContent
        });
        checkIsReplyPopedOut();
      },
      toolbarContent: GlobalTicketConversationUsers,
      showWorklog: true,
      aiPrompts: props?.prompts,
      showAIRephrase: true,
      workItem: {
        ticketId: ticketId,
        subject: data?.subject,
        source: data?.source,
        id: ticketId,
        displayId: data?.displayId,
        client: data?.client,
        additionalRequester: data?.additionalRequester
      }
    };
    setSendReplyLoading(false);
    setIsReplyPopedOut(true);
    EventEmitter.dispatch('createGlobalConversation', CHAT_PARAMS);
  };

  const getCardVariant = () => {
    if (fieldsApproved || !actions) {
      return 'successLeft';
    }
    if (isBothFieldsAndReplyPresent) {
      return 'attentionLeft';
    }
    if (replyResponse) {
      return 'infoLeft';
    }
    if (closureFieldsData || techGroupData) {
      return 'infoLeft';
    }
    return '';
  };

  const gradientVariant = getCardVariant();

  const fieldWrapperClassNames = [
    'field-card scroll overflow-auto h-auto',
    formattedReplyContent && 'mb10 max-height-150'
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (!isEmpty(fieldsMeta)) {
      initializeClosureFields();
    }
  }, [fieldsMeta]);

  useEffect(() => {
    checkIsReplyPopedOut();
  }, [globalConversations]);

  useEffect(() => {
    if (data?.aiAgentActionData?.action) {
      if (data?.aiAgentActionData?.action?.length > 0) {
        setActions(data?.aiAgentActionData?.action);
        formatActions(data?.aiAgentActionData?.action);
      }
    }
  }, []);

  return (
    <div className="w100 triage-ticket-card">
      <Card padding="none">
        <div className="pt20  pb20">
          <TicketDetails TicketIdComponent={TicketIdComponent} data={data} />

          <>
            <div className="pl20 pr-20">
              <Card gradientVariant={gradientVariant} noShadow>
                <div className="field-reply-card p10 br-5 flex fcol height300">
                  {(!isEmpty(fieldsData) || !isEmpty(techGroupData)) && (
                    <div className={fieldWrapperClassNames}>
                      {isFieldsDisliked ? (
                        <DislikeCardContent />
                      ) : (
                        <>
                          <div className="flex g5 actr mb10">
                            <div className="flex">
                              <SVGIcon name="CustomFields" size="sm" />
                            </div>
                            <div className="fsize12">
                              <TextStyle variation="subdued">
                                <FormattedMessage id="ai.ticket.triage.fields.heading" />
                              </TextStyle>
                            </div>
                          </div>
                          {!isEmpty(techGroupData) && (
                            <div className="flex g10 mb10">
                              <CustomLabel
                                label={getTranslatedText(
                                  intl,
                                  'entity.technicianGroup'
                                )}
                              />
                              <div className="w100 fsize12">
                                <AsyncPropSelector
                                  defaultValue={techGroupData}
                                  hideTitle
                                  field={{
                                    name: 'techGroup.name',
                                    label: 'techGroup.name'
                                  }}
                                  updateSelection={(f, val) => {
                                    setTechGroupData(val);
                                  }}
                                  isUiDesignSystem
                                />
                              </div>
                            </div>
                          )}

                          {!isEmpty(fieldsData) && (
                            <React.Fragment key={ticketId}>
                              <ClosureFields
                                key={ticketId}
                                fields={fieldsData}
                                onChange={onClosureFieldsChange}
                                isUiDesignSystem
                                updateAllFields={updateAllFields}
                                ticket={{
                                  ticketId,
                                  ...fieldsData
                                }}
                                customTextFieldUIProps={{
                                  inputVariant: 'default',
                                  bgTransparent: true
                                }}
                              />
                            </React.Fragment>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {isEmpty(fieldsData) &&
                    isEmpty(techGroupData) &&
                    (fieldsApproved || !actions) && (
                      <div
                        className={`flex actr jctr col p20 g10 ${
                          !formattedReplyContent ? 'fhp' : ''
                        }`}
                      >
                        <div>
                          <SVGIcon name="SuccessRound" size="xl" />
                        </div>
                        <div>
                          <TextStyle>
                            <FormattedMessage id="ai.ticket.fields.mapping.success.message" />
                          </TextStyle>
                        </div>
                      </div>
                    )}

                  {formattedReplyContent && (
                    <>
                      {isBothFieldsAndReplyPresent && (
                        <div className="divider mb10" />
                      )}
                      {isReplyDisliked ? (
                        <DislikeCardContent />
                      ) : (
                        <div className="reply-card-wrapper overflow-auto flex-1">
                          <div className="flex g5 actr mb10">
                            <div className="flex">
                              <SVGIcon name="SendWhite" size="sm" />
                            </div>
                            <div className="fsize12">
                              <TextStyle variation="subdued">
                                <FormattedMessage id="common.suggested.email" />
                              </TextStyle>
                            </div>
                          </div>

                          <div
                            className="fsize12 replyContent"
                            dangerouslySetInnerHTML={{
                              __html: dompurifier(formattedReplyContent)
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            </div>
            {actions && (
              <div
                className="flex actr fx-sb pr-20 mt10 pl20"
                id="action-buttons"
              >
                <div className="flex actr g10">
                  {(!isEmpty(fieldsData) || !isEmpty(techGroupData)) && (
                    <Button
                      icon={<SVGIcon name="LikeThumb" />}
                      size="slim"
                      onClick={updateTicketFields}
                      loading={fieldsUpdating}
                    >
                      <FormattedMessage id="common.approve" />
                    </Button>
                  )}

                  {!isEmpty(replyResponse) && (
                    <Button
                      icon={<SVGIcon name="Email" />}
                      size="slim"
                      onClick={sendReplyContent}
                      loading={sendReplyLoading || conversationLoading}
                    >
                      <FormattedMessage id="common.insert.email" />
                    </Button>
                  )}
                </div>
                <div>
                  {actions && (!fieldsApproved || !isReplyPopedOut) && (
                    <TriageFeedbackWithPopup
                      ticketId={ticketId}
                      defaultFeedbackType={triageReviewSourceAction}
                      getSuggestedFields={getFieldSuggestions}
                      onComplete={onFeedbackComplete}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        </div>
      </Card>
    </div>
  );
};

const TriageDisLikeCard = () => {
  return (
    <div className="pr-20">
      <div className="flex col  actr jctr g10 height300">
        <div>
          <SVGIcon name="ThumbsDownOutline" size="xl" />
        </div>

        <div className="mb10">
          <TextStyle>
            <FormattedMessage id="ai.ticket.triage.stopped.working.message" />
          </TextStyle>
        </div>

        <div>
          <Banner status="warning" hideIcon>
            <div className="flex g5">
              <div className="flex actr">
                <SVGIcon name="Restrict" />
              </div>

              <div>
                <TextStyle>
                  <FormattedMessage id="ai.ticket.triage.stopped.working" />
                </TextStyle>
              </div>
            </div>
          </Banner>
        </div>
      </div>

      <div className="flex jend g5">
        <div className="flex actr">
          <SVGIcon name="ThumbsDownOutline" color="red" />
        </div>

        <div>
          <TextStyle variation="negative">
            <FormattedMessage id="ticket.details.runbook.rejected" />
          </TextStyle>
        </div>
      </div>
    </div>
  );
};

const TicketTriageCardWrapper = (props) => {
  const dislikedTickets = props.dislikedTickets || [];
  const data = props?.data;
  const ticketId = data?.ticketId;
  const getDislikedTicketData = () => {
    const isCurrentTicketDisliked = dislikedTickets.find(
      (el) => el?.ticketId === ticketId
    );
    return isCurrentTicketDisliked;
  };

  const dislikedTicketData = getDislikedTicketData();
  console.log('ash:: dislikedTicketData', dislikedTicketData);
  const isDisliked = dislikedTicketData?.isDisliked;
  const loading = props.loading;
  const TicketIdComponent = props?.ticketIdComponent || <></>;

  if (loading || isDisliked) {
    return (
      <div className="w100 cursor-not-allowed triage-ticket-card">
        <Card padding="none" variant={isDisliked ? 'disabled' : ''}>
          <div className="pt20">
            <TicketDetails
              isDisliked
              TicketIdComponent={TicketIdComponent}
              data={data}
            />

            <div className="p20">
              {loading && (
                <div className="height300">
                  <SkeletonBodyText lines={3} />
                </div>
              )}
              {!loading && isDisliked && <TriageDisLikeCard />}
            </div>
          </div>
        </Card>
      </div>
    );
  }
  return (
    <TriageTicketCard {...props} dislikedTicketData={dislikedTicketData} />
  );
};

export default TicketTriageCardWrapper;
